import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage, LangCode } from "@/contexts/LanguageContext";
import rupeeVikasLogo from "/rupeevikas-logo.png";

const LANGUAGE_CARDS: { code: LangCode; native: string; english: string }[] = [
  { code: "kn", native: "ಕನ್ನಡ", english: "Kannada" },
  { code: "hi", native: "हिन्दी", english: "Hindi" },
  { code: "en", native: "English", english: "English" },
  { code: "te", native: "తెలుగు", english: "Telugu" },
  { code: "ta", native: "தமிழ்", english: "Tamil" },
  { code: "mr", native: "मराठी", english: "Marathi" },
  { code: "gu", native: "ગુજરાતી", english: "Gujarati" },
  { code: "ml", native: "മലയാളം", english: "Malayalam" },
  { code: "bn", native: "বাংলা", english: "Bengali" },
  { code: "pa", native: "ਪੰਜਾਬੀ", english: "Punjabi" },
  { code: "or", native: "ଓଡ଼ିଆ", english: "Odia" },
  { code: "as", native: "অসমীয়া", english: "Assamese" },
];

// Traditional Diya/Lamp SVG icon
function DiyaIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Flame */}
      <ellipse cx="32" cy="16" rx="5" ry="10" fill="currentColor" opacity="0.6" />
      <ellipse cx="32" cy="18" rx="3" ry="6" fill="currentColor" opacity="0.8" />
      {/* Lamp body */}
      <path d="M20 36 Q20 28 32 28 Q44 28 44 36 L46 44 Q46 48 32 48 Q18 48 18 44 Z" fill="currentColor" opacity="0.5" />
      {/* Base */}
      <ellipse cx="32" cy="48" rx="16" ry="4" fill="currentColor" opacity="0.4" />
      {/* Wick */}
      <rect x="31" y="24" width="2" height="6" rx="1" fill="currentColor" opacity="0.7" />
    </svg>
  );
}

// Handshake icon
function SathiHandshake({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M12 32 Q16 24 24 28 L32 32 L40 28 Q48 24 52 32" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.4" />
      <path d="M24 28 L20 36 Q24 40 32 38 Q40 40 44 36 L40 28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="currentColor" opacity="0.2" />
      <circle cx="16" cy="32" r="4" fill="currentColor" opacity="0.3" />
      <circle cx="48" cy="32" r="4" fill="currentColor" opacity="0.3" />
    </svg>
  );
}

const LOADING_MESSAGES: Record<string, string> = {
  en: "Loading your Sathi...",
  hi: "आपका साथी लोड हो रहा है...",
  kn: "ನಿಮ್ಮ ಸಾಥಿ ಲೋಡ್ ಆಗುತ್ತಿದೆ...",
  te: "మీ సాథీ లోడ్ అవుతోంది...",
  ta: "உங்கள் சாதி ஏற்றப்படுகிறது...",
  mr: "तुमचा साथी लोड होत आहे...",
  gu: "તમારો સાથી લોડ થઈ રહ્યો છે...",
  ml: "നിങ്ങളുടെ സാഥി ലോഡ് ചെയ്യുന്നു...",
  bn: "আপনার সাথী লোড হচ্ছে...",
  pa: "ਤੁਹਾਡਾ ਸਾਥੀ ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ...",
  or: "ଆପଣଙ୍କ ସାଥୀ ଲୋଡ ହେଉଛି...",
  as: "আপোনাৰ সাথী লোড হৈ আছে...",
};

export function LanguageGateway() {
  const { setLang } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedLang, setSelectedLang] = useState<LangCode | null>(null);

  useEffect(() => {
    const hasChosen = localStorage.getItem("vitta-lang-chosen");
    if (!hasChosen) {
      setVisible(true);
    }
  }, []);

  const handleSelect = (code: LangCode) => {
    setSelectedLang(code);
    setLoading(true);
    setLang(code);
    localStorage.setItem("vitta-lang-chosen", "true");

    // Show loading animation for 1.8s then dismiss
    setTimeout(() => {
      setLoading(false);
      setVisible(false);
    }, 1800);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto"
          style={{ background: "hsl(150 40% 12%)" }}
        >
          {/* Loading Overlay */}
          <AnimatePresence>
            {loading && selectedLang && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="absolute inset-0 z-[110] flex flex-col items-center justify-center"
                style={{ background: "hsl(150 40% 10%)" }}
              >
                {/* Spinning Diya */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="mb-6"
                >
                  <div className="h-20 w-20 rounded-full border-4 border-transparent flex items-center justify-center"
                    style={{ borderTopColor: "hsl(43 74% 53%)", borderRightColor: "hsl(43 74% 53% / 0.3)" }}>
                    <DiyaIcon className="h-10 w-10 text-amber-400" />
                  </div>
                </motion.div>

                {/* Loading text */}
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-white font-display font-bold text-xl"
                >
                  {LOADING_MESSAGES[selectedLang] || LOADING_MESSAGES.en}
                </motion.p>

                {/* Pulsing dots */}
                <div className="flex gap-1.5 mt-4">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className="h-2 w-2 rounded-full"
                      style={{ background: "hsl(43 74% 53%)" }}
                      animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
                      transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Background cultural icons pattern */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {/* Scattered Diyas */}
            <DiyaIcon className="absolute top-[10%] left-[8%] h-16 w-16 text-white/[0.04]" />
            <SathiHandshake className="absolute top-[20%] right-[12%] h-20 w-20 text-white/[0.04]" />
            <DiyaIcon className="absolute bottom-[15%] right-[8%] h-14 w-14 text-white/[0.04]" />
            <SathiHandshake className="absolute bottom-[25%] left-[10%] h-18 w-18 text-white/[0.04]" />
            <DiyaIcon className="absolute top-[50%] left-[50%] h-24 w-24 text-white/[0.03] -translate-x-1/2 -translate-y-1/2" />
          </div>

          {/* Central watermark */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03]">
            <img src={rupeeVikasLogo} alt="" width={500} height={500} fetchPriority="high" />
          </div>

          <div className="relative w-full max-w-4xl mx-auto px-4 py-10 flex flex-col items-center">
            {/* Logo */}
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.6, type: "spring" }}
              className="mb-5"
            >
              <div className="h-18 w-18 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/10 p-3">
                <img src={rupeeVikasLogo} alt="RupeeVikas" width={48} height={48} />
              </div>
            </motion.div>

            {/* Brand name */}
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-white font-display font-bold text-3xl md:text-4xl mb-2 tracking-tight"
            >
              RupeeVikas
            </motion.h1>

            {/* Indian Made Badge */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="rounded-2xl px-8 py-4 text-center mb-6 border"
              style={{
                background: "linear-gradient(135deg, hsl(43 74% 53% / 0.15), hsl(43 74% 53% / 0.05))",
                borderColor: "hsl(43 74% 53% / 0.3)",
              }}
            >
              <p className="text-2xl mb-1">🇮🇳</p>
              <p className="font-display font-bold text-white text-lg">
                Proudly Made in India
              </p>
              <p className="text-white/70 text-sm mt-1">
                अपनी भाषा चुनें • Choose your language
              </p>
            </motion.div>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-white/60 text-sm mb-8 text-center max-w-md"
            >
              सबके लिए, अपनी भाषा में — Financial freedom for every corner of Bharat
            </motion.p>

            {/* Language Grid */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.45, duration: 0.5 }}
              className="w-full"
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {LANGUAGE_CARDS.map((lang, i) => (
                  <motion.button
                    key={lang.code}
                    initial={{ scale: 0.85, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.5 + i * 0.04, duration: 0.35, type: "spring" }}
                    onClick={() => handleSelect(lang.code)}
                    className="group relative rounded-xl p-5 text-center transition-all duration-300 hover:scale-[1.04] active:scale-[0.97] overflow-hidden border-2"
                    style={{
                      background: "hsl(0 0% 100% / 0.08)",
                      backdropFilter: "blur(8px)",
                      borderColor: "hsl(0 0% 100% / 0.1)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "hsl(43 74% 53% / 0.7)";
                      e.currentTarget.style.background = "hsl(0 0% 100% / 0.14)";
                      e.currentTarget.style.boxShadow = "0 0 20px hsl(43 74% 53% / 0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "hsl(0 0% 100% / 0.1)";
                      e.currentTarget.style.background = "hsl(0 0% 100% / 0.08)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    {/* Card background cultural icon */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.05] group-hover:opacity-[0.08] transition-opacity">
                      <DiyaIcon className="h-12 w-12 text-white" />
                    </div>

                    <p className="text-white font-display font-bold text-xl leading-tight relative z-10">
                      {lang.native}
                    </p>
                    <p className="text-white/50 text-xs mt-1.5 relative z-10 group-hover:text-white/70 transition-colors">
                      {lang.english}
                    </p>
                  </motion.button>
                ))}
              </div>
            </motion.div>

            {/* Continue in English shortcut */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.4 }}
              onClick={() => handleSelect("en")}
              className="mt-8 px-8 py-3 rounded-xl font-display font-semibold text-sm transition-all duration-300 shadow-lg border"
              style={{
                background: "hsl(43 74% 53%)",
                color: "hsl(150 40% 10%)",
                borderColor: "hsl(43 74% 63%)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "hsl(43 74% 60%)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "hsl(43 74% 53%)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              Continue in English →
            </motion.button>

            {/* Trust line */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              className="mt-8 text-center text-white/40 text-xs leading-relaxed max-w-lg font-body"
            >
              🔒 RupeeVikas supports 12 Indian languages. Your data stays private. No ads, no tracking.
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
